import React, { useMemo, useState, useRef, useEffect } from 'react';
import { MoonLoader } from 'react-spinners';
import clsx from 'clsx';
import { debounce, isEmpty, isEqual } from 'lodash';
import { IconButton, Typography, useFitScale } from '@coolremote/shared';
import { Schedule as ScheduleOffIcon } from '@material-ui/icons';
import { t } from 'ttag';
import { useStoreActions } from '@models/RootStore';
import { useGetBacnetUiTemplate } from '@hooks/useBacnetUiTemplatesApi';
import { useGetBacnetParamTemplate } from '@hooks/useBacnetParamTemplatesApi';
import { useSetBacnetObjectValue } from '@hooks/useBacnetDevicesApi';
import MappingArea from '@components/AddEditBacnetUiTemplateDialog/TemplateArea/MappingArea';
import { mergeUiTemplatePointsWithParamTemplate } from '@components/AddEditBacnetUiTemplateDialog/constants';
import { useBacnetSchedules } from '@hooks/useBacnetSchedules';
import useUniversalUnitShape from '@hooks/useUniversalUnitShape';
import { withLatestOperationalData } from '@utils/latestOperationalData';
import UnitPanel from '../UnitPanel/UnitPanel';
import BacnetUnitControl from './BacnetUnitControl';

import useStyles from './styles';

const getItemsScale = (scale) => {
  if(scale > 1){
    return 1/scale;
  }
  return scale;
};

// Logical (unscaled) size of the mapping area, i.e. the coordinate space the
// UI template's point positions are stored in.
const CANVAS_WIDTH = 486;
const CANVAS_HEIGHT = 495;
// Horizontal breathing room kept beside the canvas when width is the limit.
const CANVAS_HORIZONTAL_GUTTER = 16;

const BacnetControlPanel = ({ entry, isCelsius, tempSymbol, siteTemp, navigateToSchedules }) => {
  const { title, name, id } = entry;
  const { activeSchedules } = useBacnetSchedules(id);

  const addWebSocketListener = useStoreActions(actions => actions.addWebSocketListener);
  const removeWebSocketListener = useStoreActions(actions => actions.removeWebSocketListener);

  // Fit the fixed-size mapping area into the panel body on both axes, so it
  // uses as much of the available space as possible without being cropped.
  const { containerRef: mappingAreaContainerRef, scale: mappingAreaScale } = useFitScale(
    CANVAS_WIDTH + CANVAS_HORIZONTAL_GUTTER,
    CANVAS_HEIGHT
  );

  const classes = useStyles({inverseScale:getItemsScale(mappingAreaScale)});

  // Start from what arrived over the websocket since the entry was fetched, not just the entry itself
  const [dataPointsValues, setDataPointsValues] = useState(() =>
    withLatestOperationalData(entry?.id, entry?.operationalData)
  );

  useEffect(() => {
    if (!entry?.operationalData) {
      return;
    }
    const next = withLatestOperationalData(entry?.id, entry.operationalData);
    if (!isEqual(dataPointsValues, next)) {
      setDataPointsValues(next);
    }
  }, [entry?.operationalData]);

  const uiTemplateId = typeof entry?.uiTemplate === 'string' ? entry?.uiTemplate : null;
  const { data: uiTemplateData, isLoading: isLoadingUiTemplate } = useGetBacnetUiTemplate(
    uiTemplateId
  );
  const uiTemplate = typeof entry?.uiTemplate === 'object' && entry?.uiTemplate !== null ? entry.uiTemplate : uiTemplateData;

  const paramTemplateId = typeof entry?.paramTemplate === 'string' ? entry?.paramTemplate : uiTemplate?.paramTemplate;
  const shouldFetchParamTemplate = typeof paramTemplateId === 'string' && !entry?.paramTemplateData && typeof entry?.paramTemplate !== 'object';
  const { data: paramTemplateData, isLoading: isLoadingParamTemplate } = useGetBacnetParamTemplate(
    shouldFetchParamTemplate ? paramTemplateId : null
  );
  const paramTemplate = typeof entry?.paramTemplate === 'object' && entry?.paramTemplate !== null
    ? entry.paramTemplate
    : paramTemplateData || (entry?.paramTemplateData ? { entityTemplate: entry.paramTemplateData?.entityTemplate === true, data: entry.paramTemplateData } : undefined);

  const { mutate: setObjectValue } = useSetBacnetObjectValue();

  const {
    synthesizedUnit,
    isUniversalUnit,
    hasUniversalControls,
    unitPanelStoreProps,
    handleSetActiveOperationStatus,
    handleSetActiveSetpoint,
    handleSetActiveOperationMode,
    handleSetActiveFanMode,
    handleSetActiveSwingMode,
    handleNavigateToSchedules,
  } = useUniversalUnitShape({
    entry,
    paramTemplate,
    navigateToSchedules,
    isCelsius,
    protocol: 'bacnet',
  });

  // debounce setObjectValue so it doesn't fire on every change
  const debouncedSetObjectValue = useRef(
    debounce((objectId, value) => {
      setObjectValue({
        unitId: entry.id,
        payload: {
          object: objectId,
          value: value,
        },
      });
    }, 300) // 300ms debounce delay
  ).current;

  const onUpdateObjectValue = (objectId, value) => {
    const preparedValue = prepareObjectValue(objectId, value);
    setDataPointsValues((prev) => ({
      ...prev,
      [`${objectId}/85`]: preparedValue,
      [objectId]: preparedValue,
    }));

    // Call the debounced mutation
    debouncedSetObjectValue(objectId, preparedValue);
  };

  const prepareObjectValue = (objectId, value) => {
    try {
      if (objectId.startsWith('binary')) {
        if (typeof value === 'string') {
          value = parseInt(value);
        }
      }
    } catch (error) {
      console.error('Error parsing object value:', error);
    }
    return value;
  };

  const temperatureDisplay = useMemo(() => {
    if (isCelsius && !siteTemp?.C) {
      return null;
    }

    if (!isCelsius && !siteTemp?.F) {
      return null;
    }

    // Determine the temperature and symbol to display
    const temperature = isCelsius
      ? Math.round(siteTemp.C)
      : Math.round(siteTemp.F);

    return (
      <>
        <span>{temperature}</span>
        <span className={classes.scaleStyle}>{tempSymbol}</span>
      </>
    );
  }, [isCelsius, siteTemp, tempSymbol, classes.scaleStyle]);

  // Merge with paramTemplate configuration
  const mergedDataPoints = useMemo(() => {
    return mergeUiTemplatePointsWithParamTemplate(
      paramTemplate?.data,
      uiTemplate?.data?.points
    );
  }, [paramTemplate, uiTemplate]);

  useEffect(() => {
    const handleWebSocketMessage = (message) => {
      if (
        message.name !== 'UPDATE_BACNET_UNIT_DATA' ||
        String(message.data?.unitId) !== String(id)
      ) {
        return;
      }

      const { operationalData } = message.data;
      if(isEmpty(operationalData)) {
        return;
      }

      // BACnet pushes only the points that changed, so merge over what we already have
      setDataPointsValues((prev) => {
        const merged = { ...prev, ...operationalData };
        return isEqual(prev, merged) ? prev : merged;
      });
    };

    // Subscribe to websocket messages
    addWebSocketListener(handleWebSocketMessage);

    // Cleanup subscription on unmount
    return () => {
      removeWebSocketListener(handleWebSocketMessage);
    };
  }, []);

  const handleSchedulesClick = () => {
    if (navigateToSchedules) {
      navigateToSchedules();
    }
  };

  const isLoadingTemplates =
    (uiTemplateId && isLoadingUiTemplate) || (shouldFetchParamTemplate && isLoadingParamTemplate);

  const showUniversalFallback = !uiTemplate && isUniversalUnit;

  const rawPointsData = useMemo(() => {
    return (
      paramTemplate?.data ||
      entry?.paramTemplateData?.data ||
      entry?.paramTemplateData ||
      entry?.data ||
      {}
    );
  }, [paramTemplate, entry?.paramTemplateData, entry?.data]);

  const mappedPointsList = useMemo(() => {
    if (!rawPointsData || typeof rawPointsData !== 'object') {
      return [];
    }
    const entries = Object.entries(rawPointsData).filter(
      ([key, val]) =>
        val &&
        typeof val === 'object' &&
        key !== 'entityTemplate' &&
        key !== 'entityType'
    );

    return entries.map(([objectId, pointConfig]) => {
      const config = pointConfig || {};
      const { step, min, max } = config.customEnum || {};
      return {
        objectId,
        name: config.name || config.label || objectId,
        shortId: config.shortId,
        readOnly: config.readOnly ?? false,
        units: config.userUnits ?? config.units,
        userUnits: config.userUnits,
        minValue: config.minValue ?? (min != null ? parseFloat(min) : null),
        maxValue: config.maxValue ?? (max != null ? parseFloat(max) : null),
        step: config.step ?? (step != null ? parseFloat(step) : null),
        customEnum: config.customEnum,
        customEnumVisibility: config.customEnumVisibility,
        property: config.property,
      };
    });
  }, [rawPointsData]);

  const renderBody = () => {
    if (isLoadingTemplates) {
      return (
        <div className={classes.loaderContainer}>
          <MoonLoader size={50} color="#A1899F" />
        </div>
      );
    }

    if (uiTemplate) {
      return (
        <div
          className={classes.scalableMappingArea}
          style={{
            // Centered horizontally: shift back by half its own width, then scale
            // from the top-center origin (see scalableMappingArea).
            transform: `translateX(-50%) scale(${mappingAreaScale})`,
          }}
        >
          <MappingArea
            currentScale={mappingAreaScale}
            dataPoints={mergedDataPoints}
            onChangeControlValue={onUpdateObjectValue}
            templateImg={uiTemplate?.fileURL}
            mode="control"
            dataPointsValues={dataPointsValues}
          />
        </div>
      );
    }

    if (isUniversalUnit) {
      if (hasUniversalControls && synthesizedUnit) {
        return (
          <UnitPanel
            {...unitPanelStoreProps}
            unit={synthesizedUnit}
            isCelsius={isCelsius}
            tempSymbol={tempSymbol}
            siteTemp={siteTemp}
            setActiveOperationStatus={handleSetActiveOperationStatus}
            setActiveSetpoint={handleSetActiveSetpoint}
            setActiveOperationMode={handleSetActiveOperationMode}
            setActiveFanMode={handleSetActiveFanMode}
            setActiveSwingMode={handleSetActiveSwingMode}
            navigateToSchedules={handleNavigateToSchedules}
          />
        );
      }

      return (
        <div className={classes.emptyState}>
          {t`No universal controls configured for this unit.`}
        </div>
      );
    }

    if (!mappedPointsList.length) {
      return (
        <div className={classes.emptyState}>
          <Typography className={classes.emptyStateText}>
            {t`No BACnet parameters mapped for this unit.`}
          </Typography>
        </div>
      );
    }

    return (
      <BacnetUnitControl
        unitName={title ?? name}
        points={mappedPointsList}
        operationalData={dataPointsValues}
        onSetValue={onUpdateObjectValue}
      />
    );
  };

  return (
    <div
      className={clsx(
        classes.bacnetControlPanel,
        uiTemplate && classes.bacnetControlPanel_fitToPanel
      )}
    >
      {/* UnitPanel renders its own name, site temperature and schedules
          button, so the panel header would only duplicate them. */}
      {!showUniversalFallback && (
        <div className={classes.bacnetControlPanel__header}>
          {temperatureDisplay && (
            <div className={classes.bacnetControlPanel__temperature}>{temperatureDisplay}</div>
          )}
          <div className={classes.bacnetControlPanel__unitName}>{title ?? name}</div>
          {navigateToSchedules && (
            <div className={classes.bacnetControlPanel__scheduleButton}>
              <IconButton
                onClick={handleSchedulesClick}
                className={classes.scheduleButton}
                title="Manage Schedules"
              >
                <ScheduleOffIcon fontSize="medium" />
                {activeSchedules.length > 0 && (
                  <span className={classes.badgeStyle}>{activeSchedules.length}</span>
                )}
              </IconButton>
            </div>
          )}
        </div>
      )}
      <div
        className={clsx(
          classes.bacnetControlPanel__body,
          uiTemplate && classes.bacnetControlPanel__body_fitToPanel
        )}
        ref={mappingAreaContainerRef}
      >
        {renderBody()}
      </div>
    </div>
  );
};

export default BacnetControlPanel;
